import { DEFAULT_LANGUAGE, DEFAULT_THEME, LANGUAGES, THEMES } from '../config/app';

export const USER_PREFS_STORAGE_PREFIX = 'bolita.userPrefs:';
export const GUEST_PREFS_USER_ID = '__guest__';
export const USER_PREFS_VERSION = 1;

export type StoredLanguage = 'en' | 'es';
export type StoredTheme = 'light' | 'dark' | 'system';

export type QuietHoursPrefs = {
  enabled: boolean;
  from: string;
  to: string;
};

export type NotificationPrefs = {
  prefs: Record<string, boolean>;
  quietHours: QuietHoursPrefs;
  sound: boolean;
  pushEnabled: boolean;
};

export type UserPreferences = {
  v: typeof USER_PREFS_VERSION;
  lang: StoredLanguage;
  theme: StoredTheme;
  notifications?: NotificationPrefs;
};

export function resolvePrefsUserId(userId: string | null | undefined): string {
  return userId?.trim() ? userId : GUEST_PREFS_USER_ID;
}

export function defaultQuietHours(): QuietHoursPrefs {
  return { enabled: false, from: '22:00', to: '07:00' };
}

export function defaultUserPreferences(): UserPreferences {
  return {
    v: USER_PREFS_VERSION,
    lang: DEFAULT_LANGUAGE,
    theme: DEFAULT_THEME,
    notifications: {
      prefs: {},
      quietHours: defaultQuietHours(),
      sound: true,
      pushEnabled: true,
    },
  };
}

function isLanguage(value: unknown): value is StoredLanguage {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

function isTheme(value: unknown): value is StoredTheme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

function parseQuietHours(value: unknown): QuietHoursPrefs {
  const defaults = defaultQuietHours();
  if (!value || typeof value !== 'object') return defaults;
  const raw = value as Partial<QuietHoursPrefs>;
  return {
    enabled: typeof raw.enabled === 'boolean' ? raw.enabled : defaults.enabled,
    from: typeof raw.from === 'string' ? raw.from : defaults.from,
    to: typeof raw.to === 'string' ? raw.to : defaults.to,
  };
}

function parseNotificationPrefs(value: unknown): NotificationPrefs {
  const defaults = defaultUserPreferences().notifications!;
  if (!value || typeof value !== 'object') return defaults;
  const raw = value as Partial<NotificationPrefs>;
  const prefs: Record<string, boolean> = {};
  if (raw.prefs && typeof raw.prefs === 'object') {
    Object.entries(raw.prefs).forEach(([key, enabled]) => {
      if (typeof enabled === 'boolean') prefs[key] = enabled;
    });
  }
  return {
    prefs,
    quietHours: parseQuietHours(raw.quietHours),
    sound: typeof raw.sound === 'boolean' ? raw.sound : defaults.sound,
    pushEnabled: typeof raw.pushEnabled === 'boolean' ? raw.pushEnabled : defaults.pushEnabled,
  };
}

export function parseUserPreferences(raw: unknown): UserPreferences | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Partial<UserPreferences>;
  if (!isLanguage(data.lang) || !isTheme(data.theme)) return null;
  return {
    v: USER_PREFS_VERSION,
    lang: data.lang,
    theme: data.theme,
    notifications: parseNotificationPrefs(data.notifications),
  };
}

export function mergeRoleNotificationPrefs(
  roleDefaults: Record<string, boolean>,
  stored?: Record<string, boolean>,
): Record<string, boolean> {
  const merged = { ...roleDefaults };
  if (!stored) return merged;
  Object.keys(merged).forEach((key) => {
    if (typeof stored[key] === 'boolean') merged[key] = stored[key];
  });
  return merged;
}

export function readUserPreferences(userId: string): UserPreferences | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${USER_PREFS_STORAGE_PREFIX}${userId}`);
    if (!raw) return null;
    return parseUserPreferences(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeUserPreferences(
  userId: string,
  patch: Partial<UserPreferences>,
): UserPreferences {
  const current = readUserPreferences(userId) ?? defaultUserPreferences();
  const next: UserPreferences = {
    ...current,
    ...patch,
    notifications: patch.notifications
      ? {
          ...current.notifications!,
          ...patch.notifications,
          prefs: patch.notifications.prefs ?? current.notifications?.prefs ?? {},
          quietHours: patch.notifications.quietHours ?? current.notifications?.quietHours ?? defaultQuietHours(),
        }
      : current.notifications,
  };

  try {
    localStorage.setItem(`${USER_PREFS_STORAGE_PREFIX}${userId}`, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }

  return next;
}

export function loadLocalePreferences(userId: string | null | undefined): Pick<UserPreferences, 'lang' | 'theme'> {
  const prefsUserId = resolvePrefsUserId(userId);
  const stored = readUserPreferences(prefsUserId);
  if (stored) {
    return { lang: stored.lang, theme: stored.theme };
  }

  if (userId) {
    const guest = readUserPreferences(GUEST_PREFS_USER_ID);
    if (guest) return { lang: guest.lang, theme: guest.theme };
  }

  return { lang: DEFAULT_LANGUAGE, theme: DEFAULT_THEME };
}

export function loadNotificationPreferences(
  roleDefaults: Record<string, boolean>,
  userId: string | null | undefined,
): NotificationPrefs {
  const stored = userId ? readUserPreferences(userId)?.notifications : null;
  return {
    prefs: mergeRoleNotificationPrefs(roleDefaults, stored?.prefs),
    quietHours: stored?.quietHours ?? defaultQuietHours(),
    sound: stored?.sound ?? true,
    pushEnabled: stored?.pushEnabled ?? true,
  };
}

export function saveNotificationPreferences(
  userId: string,
  notifications: NotificationPrefs,
): void {
  writeUserPreferences(userId, { notifications });
}
